import { ENVIRONMENTS } from '#lib/constants.js';
import { isFunction } from '#lib/utils/function.js';
import { get } from '#lib/utils/object.js';
import { isString } from '#lib/utils/string.js';

const maxLevels = 10;
const makeEmptyPath = () => new Array(maxLevels).fill(undefined);

const access = () => {
    let path = makeEmptyPath();
    let currIndex = 0;

    const add = (newProp) => {
        path[currIndex] = newProp;
        currIndex++;
        return { path, index: currIndex };
    };

    const close = () => {
        const lastPath = path;
        const lastIndex = currIndex;
        path = makeEmptyPath();
        currIndex = 0;
        return { path: lastPath, index: lastIndex };
    };

    return {
        add,
        close
    };
};

function parse(string, params) {
    const regexp = /%([0-9]+)\$/g;
    let match;
    while ((match = regexp.exec(string))) {
        const index = match[1] - 1;
        const param = params[index] ?? '';
        string = string.replace(match[0], param);
    }
    return string;
}

function getGroupIndex(node) {
    const index = Number(node._index);

    if (typeof index !== 'number') {
        if (ENV === ENVIRONMENTS.DEVELOPMENT)
            throw new Error(`Intl, getGroupIndex, invalid index ${index} at node ${node}`);
        return '';
    }
    return index;
}

function getPlural(node, language, params) {
    const pluralRules = new Intl.PluralRules(language);
    const index = getGroupIndex(node);
    const param = Number(params[index - 1]);

    if (typeof param !== 'number' || isNaN(param)) {
        if (ENV === ENVIRONMENTS.DEVELOPMENT)
            throw new Error(`Intl, getSelection, missing or invalid param ${index} at node ${node}`);
        return '';
    }

    const key = pluralRules.select(param);
    const pluralNode = node[key];

    if (!pluralNode) return '';

    return format(pluralNode, language, ...params);
}

function getSelection(node, language, params) {
    const index = getGroupIndex(node);
    const param = params[index - 1];
    if (typeof param === 'undefined') {
        if (ENV === ENVIRONMENTS.DEVELOPMENT) throw new Error(`Intl, getSelection, missing param ${index}`);
        return '';
    }

    const selectedNode = node[param];

    if (!selectedNode) {
        if (ENV === ENVIRONMENTS.DEVELOPMENT)
            throw new Error(`Intl, getSelection, missing selection for param ${param}`);
        return '';
    }

    return format(selectedNode, language, ...params);
}

function format(string, language, ...params) {
    try {
        switch (string._type) {
            case 'plurals':
                string = getPlural(string, language, params);
                break;
            case 'select':
                string = getSelection(string, language, params);
                break;
        }
        if (!isString(string)) {
            if (ENV === ENVIRONMENTS.DEVELOPMENT) throw new Error(`Text not found for key ${string}`);
            return '**text not found**';
        }

        return parse(string, params);
    } catch (error) {
        if (ENV === ENVIRONMENTS.DEVELOPMENT) throw new Error(error);
        return '**text not found**';
    }
}

const localStoragePrefix = 'abs-texts';

function getLocalStorage(key) {
    try {
        const existing = localStorage.getItem(key);
        return JSON.parse(existing);
    } catch (error) {
        console.error(error);
    }
}

function setLocalStorage(key, data) {
    try {
        const updated = JSON.stringify(data);
        localStorage.setItem(key, updated);
    } catch (error) {
        console.error(error);
    }
}

async function fetchTexts(language) {
    return fetch(`${INTL_URL}/texts-${language}.json`)
        .then((response) => response.json())
        .catch((error) => {
            console.error(error);
        });
}

const languageKey = 'language';

const traverse = access();
const emptyFn = () => ({});

export default function intl(options) {
    const { onSetup, onSuccess, setGlobal = true } = options;

    const [preferredLanguage] = navigator.language.split('-');

    let language = getLocalStorage(languageKey) ?? preferredLanguage;
    let texts;

    function getLanguage() {
        return getLocalStorage(languageKey);
    }

    async function setTexts() {
        setLocalStorage(languageKey, language);
        const textsKey = `${localStoragePrefix}-${language}`;
        texts = getLocalStorage(textsKey);
        if (!texts && isFunction(onSetup)) onSetup();
        texts = await fetchTexts(language);
        if (texts) {
            setLocalStorage(textsKey, texts);
            if (isFunction(onSuccess)) onSuccess(texts);
        }
    }

    setTexts();

    function changeLanguage(newLanguage) {
        language = newLanguage;
        setTexts();
    }

    const _t = new Proxy(emptyFn, {
        get(_, key) {
            traverse.add(key);
            return _t;
        },
        apply(_, thisArg, args) {
            const { path, index } = traverse.close();
            let string = get(texts, path);

            //TODO: Se la stringa non c'è prova a prendere la stringa nella lingua di default (se lingua corrente non è quello di default)

            if (!string) {
                if (ENV === ENVIRONMENTS.DEVELOPMENT) {
                    const readablePath = path.filter(Boolean).join('.');
                    console.warn(readablePath);
                    //throw new Error(`String not found for ${readablePath}`);
                }
                return path[index - 1];
            }

            if (args) return format(string, language, ...args);
            return string;
        }
    });
    if (setGlobal) {
        window._t = _t;
        return { changeLanguage, getLanguage };
    }

    return {
        _t,
        changeLanguage
    };
}